'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAddShipmentMethodMutation } from 'services/shoppingCartApi';

import { Box, Flex, useTheme } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setIsPreferencesMenuEnabled } from 'store/reducers/cartSlice';

import { formatCurrency } from 'utils';
import { separateItemsByOptimalFulfillment } from 'utils/helpers/fulfillmentHelpers';

import {
  AddShippingMethodParameters,
  CartShippingResponseType,
  ShipmentModeType,
  ShoppingCartResponse
} from 'types';

import { Divider, Loader } from 'ui';
import { AddressForm } from '../../../AddressForm';
import { ContentLoader } from '../../../ContentLoader';
import { PickUpLocation } from '../PickUpLocation';
import { ShipmentAddressList } from '../ShipmentAddressList';
import { ShipmentMethodItem } from '../ShipmentMethodItem';

interface ShippingListProps {
  mode: ShipmentModeType;
  setMode: (mode: ShipmentModeType) => void;
  activeStoreId?: number | null;
  invoiceId?: number;
  pickUpStoreId?: number | null;
  addrId?: number | null;
  onNextStep: () => void;
  shipmentIds: {
    invoiceId: number;
    activeStoreId: number | null;
    pickUpStoreId: number | null;
    addrId: number | null;
  };
  shippingList: CartShippingResponseType[];
  isLoading: boolean;
  isMixedDelivery: boolean;
  cartData?: ShoppingCartResponse;
}

const ShippingList = ({
  mode,
  setMode,
  shipmentIds,
  shippingList,
  onNextStep,
  isLoading,
  isMixedDelivery,
  cartData
}: ShippingListProps) => {
  const dispatch = useAppDispatch();
  const {
    colors: {
      palette: {
        shop: {
          cart: { shippingStep: styles }
        }
      }
    }
  } = useTheme();

  const [addShipmentMethod, { isLoading: isAddLoading }] =
    useAddShipmentMethodMutation();

  const [shippingId, setShippingId] = useState<number | null>();

  const { invoiceId, activeStoreId, pickUpStoreId, addrId } = shipmentIds;

  const { guestInfo } = useAppSelector((state) => state.persistGuestAuth);
  const { fulfillmentPreferences } = useAppSelector((state) => state.cart);

  const { pickupItems: pickupItemIds, shippingItems: shippingItemIds } =
    useMemo(() => {
      if (!cartData?.items) {
        return { pickupItems: [], shippingItems: [] };
      }
      return separateItemsByOptimalFulfillment(
        cartData.items,
        fulfillmentPreferences
      );
    }, [cartData?.items, fulfillmentPreferences]);

  const onClick = (id: number) => () => {
    setShippingId(id);

    if (id === -1) {
      setMode(ShipmentModeType.PICKUP);
    } else if (guestInfo) {
      setMode(ShipmentModeType.ADD_ADDRESS);
    } else {
      setMode(ShipmentModeType.SHIPMENT);
    }
  };

  const selectShipmentMethod = useCallback(
    async (params: AddShippingMethodParameters) => {
      const enhancedParams = isMixedDelivery
        ? {
            ...params,
            cart_item_id_arr:
              params.shipping_id === -1 ? pickupItemIds : shippingItemIds
          }
        : {
            ...params,
            cart_item_id_arr: [...pickupItemIds, ...shippingItemIds]
          };
      await addShipmentMethod(enhancedParams).unwrap();
    },
    [addShipmentMethod, isMixedDelivery, pickupItemIds, shippingItemIds]
  );

  const onPickLocationId = useCallback(
    async (store_id: number) => {
      await selectShipmentMethod({
        invoice_id: invoiceId,
        shipping_id: -1,
        store_id
      });
      if (isMixedDelivery) {
        dispatch(setIsPreferencesMenuEnabled(false));
      }
    },
    [dispatch, invoiceId, isMixedDelivery, selectShipmentMethod]
  );

  const onShipmentAddressId = useCallback(
    async (addr_id: number) => {
      await selectShipmentMethod({
        invoice_id: invoiceId,
        shipping_id: shippingId || 0,
        addr_id
      });
    },
    [invoiceId, selectShipmentMethod, shippingId]
  );

  const handleGuestAddrSubmit = async (addr_id: number) => {
    await onShipmentAddressId(addr_id);
    onNextStep();
  };

  const renderMode = () => {
    switch (mode) {
      case ShipmentModeType.PICKUP:
        return (
          <PickUpLocation onClick={onPickLocationId} activeId={pickUpStoreId} />
        );
      case ShipmentModeType.SHIPMENT:
        return (
          <ShipmentAddressList
            onClick={onShipmentAddressId}
            onAddHandler={() => setMode(ShipmentModeType.ADD_ADDRESS)}
            activeId={shippingId === activeStoreId ? addrId : null}
          />
        );
      case ShipmentModeType.ADD_ADDRESS:
        return (
          <AddressForm
            isShopCart
            onScreenTypeChange={() => setMode(ShipmentModeType.SHIPMENT)}
            onGuestSubmit={handleGuestAddrSubmit}
          />
        );
      default:
        return (
          <Flex
            gap='10px'
            flexWrap='wrap'
            justifyContent={shippingList.length === 1 ? 'center' : 'flex-start'}
          >
            {shippingList.map((method) => {
              const isPickupItem = method.id === -1;
              return (
                <Box
                  display={isPickupItem && isMixedDelivery ? 'none' : 'block'}
                  key={method?.id}
                  w={{
                    base: '100%',
                    md: 'calc(50% - 5px)'
                  }}
                >
                  <ShipmentMethodItem
                    h={95}
                    label={method.name}
                    cost={
                      method.cost === 0 ? 'Free' : formatCurrency(method.cost)
                    }
                    onClick={onClick(method?.id)}
                    isActive={method?.id === activeStoreId}
                    isHoverAvailable
                  />
                </Box>
              );
            })}
          </Flex>
        );
    }
  };

  useEffect(() => {
    if (shippingList.length > 0 && !shippingId) {
      // Set the first non-pickup shipping method as default
      const firstShippingMethod = shippingList.find(
        (method) => method.id !== -1
      );
      if (firstShippingMethod) {
        setShippingId(firstShippingMethod.id);
      }
    }
  }, [shippingList, shippingId]);

  return shippingList.length > 0 || isMixedDelivery ? (
    <>
      {(isAddLoading || isLoading) && <Loader fixed bg='blackOpacity.10' />}
      {renderMode()}
    </>
  ) : (
    <Box>
      <Divider bg={styles.divider} height='1px' border='none' />
      <ContentLoader
        isLoading={isLoading}
        label='List is Empty'
        minHeight='300px'
      />
      <Divider bg={styles.divider} height='1px' border='none' />
    </Box>
  );
};

export default ShippingList;
